import FeatureGuard from "@components/featureGuard/FeatureGuard"
import { mapLayersColors } from "@components/map/utils/mapLayers"
import { changeOpacity } from "./mapComponents/createCombinedTooltip"
import ToggleSwitch from "@components/toggleSwitch/ToggleSwitch"
import { ToggleStyle } from "@components/toggleSwitch/types"
import { MapFiltersFacilities } from "@redux/features/mapFiltersFacilities/mapFiltersFacilitiesSlice"
import { PortalFeature } from "api"
import { memo } from "react"
import { useTranslation } from "react-i18next"

type MapFiltersFacilitiesSub<O extends keyof MapFiltersFacilities> = MapFiltersFacilities[O]
type SubProp<O extends keyof MapFiltersFacilities> = keyof MapFiltersFacilitiesSub<O>

export type FacilitiesFiltersListProps = {
	filters: MapFiltersFacilities["facilities"]
	onFilterVisibilityChanged: <O extends keyof MapFiltersFacilities>(
		mainProp: O,
		subProp: SubProp<O>
	) => (value: boolean) => void
	showWindTurbines?: boolean
}

export const FacilitiesFiltersList = memo(
	({ filters, onFilterVisibilityChanged, showWindTurbines = true }: FacilitiesFiltersListProps) => {
		const { t } = useTranslation()

		return (
			<div className="grid">
				<ListEntry
					initialState={filters.showPorts}
					label={`${t("SHOW")} ${t("PORTS").toLowerCase()}`}
					labelColor={mapLayersColors.facilities["PORT"]}
					mainProp="facilities"
					subProp="showPorts"
					onChange={onFilterVisibilityChanged}
				/>

				<FeatureGuard featureName={PortalFeature.OIL_AND_GAS}>
					<>
						<ListEntry
							initialState={filters.showPlatforms}
							label={`${t("SHOW")} ${t("OIL AND GAS PLATFORMS").toLowerCase()}`}
							labelColor={mapLayersColors.facilities["PLATFORM"]}
							mainProp="facilities"
							subProp="showPlatforms"
							onChange={onFilterVisibilityChanged}
						/>
						<ListEntry
							initialState={filters.showDischargeTerminals}
							label={`${t("SHOW")} ${t("DISCHARGE TERMINALS").toLowerCase()}`}
							labelColor={mapLayersColors.facilities["DISCHARGE_TERMINAL"]}
							mainProp="facilities"
							subProp="showDischargeTerminals"
							onChange={onFilterVisibilityChanged}
						/>
						<ListEntry
							initialState={filters.showDischargeSTS}
							label={`${t("SHOW")} ${t("DISCHARGE TERMINALS (STS)").toLowerCase()}`}
							labelColor={mapLayersColors.facilities["DISCHARGE_STS"]}
							mainProp="facilities"
							subProp="showDischargeSTS"
							onChange={onFilterVisibilityChanged}
						/>
						<ListEntry
							initialState={filters.showAnchorage}
							label={`${t("SHOW")} ${t("ANCHORAGES").toLowerCase()}`}
							labelColor={mapLayersColors.facilities["ANCHORAGE"]}
							mainProp="facilities"
							subProp="showAnchorage"
							onChange={onFilterVisibilityChanged}
						/>
					</>
				</FeatureGuard>
				{showWindTurbines === true ? (
					<FeatureGuard featureName={PortalFeature.WIND_TURBINES}>
						<ListEntry
							initialState={filters.showWindTurbines}
							label={`${t("SHOW")} ${t("WIND FARMS").toLowerCase()}`}
							labelColor={mapLayersColors.facilities["WIND_TURBINE"]}
							mainProp="facilities"
							subProp="showWindTurbines"
							onChange={onFilterVisibilityChanged}
						/>
					</FeatureGuard>
				) : null}
				<FeatureGuard featureName={PortalFeature.FISH_FARM}>
					<ListEntry
						initialState={filters.showFishFarms}
						label={`${t("SHOW")} ${t("FISH FARMS").toLowerCase()}`}
						labelColor={mapLayersColors.facilities["FISH_FARM"]}
						mainProp="facilities"
						subProp="showFishFarms"
						onChange={onFilterVisibilityChanged}
					/>
				</FeatureGuard>
			</div>
		)
	}
)

const ListEntry = memo(
	<O extends keyof MapFiltersFacilities>({
		label,
		initialState,
		onChange,
		mainProp,
		subProp,
		labelColor,
	}: {
		label: string
		initialState: boolean
		onChange: (
			mainProp: O,
			subProp: keyof MapFiltersFacilitiesSub<O>
		) => (value: boolean) => void
		mainProp: O
		subProp: keyof MapFiltersFacilitiesSub<O>
		labelColor: string
	}) => {
		return (
			<div className="hover:bg-gray-f4 flex flex-row items-center justify-between px-2 py-1">
				<ToggleSwitch
					onChange={onChange(mainProp, subProp)}
					initialState={initialState}
					style={ToggleStyle.Rounded}
					label={label}
				/>
				<div
					style={{
						backgroundColor: changeOpacity(labelColor),
						borderColor: labelColor,
					}}
					className={`h-5 w-5 rounded-full border-2`}
				/>
			</div>
		)
	}
)
